// Softmax over cosine-similarity scores, used to turn raw cosines into a
// separability-aware probability for DinoFast classification.
//
// Raw cosine over mean-pooled DINO embeddings tends to live in a narrow band
// (often 0.7-0.95). An absolute threshold on cosine is therefore either useless
// or arbitrary, and a 0.82 vs 0.80 top-1/top-2 split feels indistinguishable
// from a 0.82 vs 0.65 split even though the latter is far more separable.
//
// Softmax with a small temperature amplifies cosine gaps into a probability:
// at T=0.07, cosines [0.82, 0.80] → ~[0.57, 0.43] (filtered by a >70% slider),
// while [0.82, 0.65] → ~[0.92, 0.08] (passes easily).
//
// Numerically stabilized by subtracting max(scores) before exp.
export function softmaxFromCosines(
  cosines: ReadonlyArray<number>,
  temperature: number,
): number[] {
  if (cosines.length === 0) return [];

  if (temperature <= 0) {
    throw new Error(
      `softmaxFromCosines: temperature must be > 0 (got ${temperature})`,
    );
  }

  const scaled = cosines.map((c) => c / temperature);
  const max = Math.max(...scaled);
  const exps = scaled.map((s) => Math.exp(s - max));
  const sum = exps.reduce((acc, v) => acc + v, 0);

  return exps.map((v) => v / sum);
}
